import { Button } from '@cian/ui-kit';
import { TButtonSize } from '@cian/ui-kit/button/types';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PopupOwnOfferContainer } from 'shared/components/popups/own_offer/container';
import { useApplicationContext } from 'shared/containers/ApplicationContext';
import { useDynamicPhoneFlow } from 'shared/hooks/useDynamicPhoneFlow';
import { useOfferDynamicCalltrackingEnabled } from 'shared/hooks/useOfferDynamicCalltrackingEnabled';
import { useSimpleBotDetector } from 'shared/hooks/useSimpleBotDetector';
import {
  selectIsOfferPublished,
  selectOffer,
  getPhones,
  getAgentAvailability,
  selectUser,
  getVillageId,
  selectIsFromSuburbanBuilder,
  isNewbuildingFromBuilderSelector,
  isNewbuildingFromSellerSelector,
} from 'shared/selectors';
import { selectEarlyAccessContactsFlowEnabled } from 'shared/selectors/earlyAccess/selectEarlyAccessContactsFlowEnabled';
import { getDynamicCalltrackingSiteBlockId } from 'shared/selectors/getDynamicCalltrackingSiteBlockId';
import { isRenterFormEnabled } from 'shared/selectors/isRenterFormEnabled';
import { selectIsChatsEnabled } from 'shared/selectors/offerChat';
import { getRosreestrInfo } from 'shared/selectors/offerData/getRosreestrInfo';
import { isPrintVersionPhoneVisibilitySelector } from 'shared/selectors/print';
import { selectAgent } from 'shared/selectors/selectAgent';
import { prefetchFormState } from 'shared/store/deal-rent';
import { setNotAvailablePopupOpenValue } from 'shared/store/notAvailablePopupOpen';
import { setPhoneCollapse } from 'shared/store/phone_collapse';
import { getPhoneButtonText } from 'shared/utils/getPhoneButtonText';
import {
  trackBlockedEarlyAccessPhoneClick,
  trackShowEarlyAccessPhoneClick,
} from 'shared/utils/tracking/earlyAccessTracking';
import { trackShowStatNumber } from 'shared/utils/tracking/trackShowStatNumber';
import { v4 as uuid } from 'uuid';

import { trackShowPhoneClick } from './tracking';
import { useEarlyAccessPaywallContext } from '../../../../packages/EarlyAccessPaywall';
import {
  EAccessCheckTrackingStatus,
  EStatus,
  ESubscriptionLimitType,
} from '../../../../packages/EarlyAccessPaywall/shared/types/context';
import { PhoneContainer } from '../PhoneContainer';

interface IPhonesContainerProps {
  size?: TButtonSize;
  trackingAction?: string;
  phonesShown: boolean;
  onPhoneClick(): void;
}

export const PhonesContainer: React.FC<React.PropsWithChildren<IPhonesContainerProps>> = ({
  size = 'M',
  trackingAction,
  onPhoneClick,
  phonesShown,
}) => {
  const dispatch = useDispatch();
  const { logger, telemetry } = useApplicationContext();
  const isPublished = useSelector(selectIsOfferPublished);
  const offer = useSelector(selectOffer);
  const phones = useSelector(getPhones);
  const isChatsEnabled = useSelector(selectIsChatsEnabled);
  const isDealRentFormEnabled = useSelector(isRenterFormEnabled);
  const agent = useSelector(selectAgent);
  const agentAvailability = useSelector(getAgentAvailability);
  const user = useSelector(selectUser);
  const siteBlockId = useSelector(getDynamicCalltrackingSiteBlockId);
  const rosreestrInfo = useSelector(getRosreestrInfo);
  const villageId = useSelector(getVillageId);
  const isSuburbanFromBuilder = useSelector(selectIsFromSuburbanBuilder);
  const isNewbuildingFromBuilder = useSelector(isNewbuildingFromBuilderSelector);
  const isNewbuildingFromSeller = useSelector(isNewbuildingFromSellerSelector);
  const buttonContent = getPhoneButtonText({
    isDealRent: isDealRentFormEnabled,
    isNewbuildingFromBuilder,
    isNewbuildingFromSeller,
    isSuburbanFromBuilder,
  });
  const isPrintVersion = useSelector(isPrintVersionPhoneVisibilitySelector);
  const botDetector = useSimpleBotDetector();

  const isAgentNotAvailable = agentAvailability && !agentAvailability.available;
  const [isOwnOfferPopupOpen, setOwnOfferPopupOpen] = React.useState(false);

  const isOfferDynamicCalltrackingEnabled = useOfferDynamicCalltrackingEnabled();
  const { requestPhone } = useDynamicPhoneFlow();
  const [isDynamicPhoneLoading, setDynamicPhoneLoading] = React.useState(false);

  const { checkContactsAccess, status } = useEarlyAccessPaywallContext();
  const isEarlyAccessContactsFlowEnabled = useSelector(selectEarlyAccessContactsFlowEnabled);

  const startChat = () => {
    const isOfferOwnedByUser = Boolean(user && user.isAuthenticated && user.realtyUserId === offer.userId);
    const canShowAvailability = isChatsEnabled && isAgentNotAvailable;

    if (!canShowAvailability) {
      return;
    }

    switch (true) {
      case isOfferOwnedByUser:
        return setOwnOfferPopupOpen(true);
      default:
        dispatch(setNotAvailablePopupOpenValue(true));
    }
  };

  const ownOfferPopupClose = () => setOwnOfferPopupOpen(false);

  const onShowPhoneClick = () => {
    if (isDealRentFormEnabled) {
      dispatch(prefetchFormState());

      return;
    }

    if (isNewbuildingFromBuilder || isNewbuildingFromSeller) {
      telemetry.increment('newbuilding.show_phone_click.bottom_author_block');
    }

    onPhoneClick();
    dispatch(setPhoneCollapse());
  };

  const trackShowPhone = (clickId?: string) => {
    const isAgentNotAvailable = agentAvailability && !agentAvailability.available;

    trackShowPhoneClick({
      trackingAction,
      offer,
      agent,
      user,
      kpId: villageId,
      bot: !botDetector.isHuman(),
      isAgentNotAvailable,
      siteBlockId,
      rosreestrInfo,
      clickId,
    });
  };

  const handleEarlyAccessFlow = async () => {
    const commonTrackingParams = { source: 'card_agent_block', offer, agent, user, siteBlockId };

    if (!user?.isAuthenticated) {
      trackBlockedEarlyAccessPhoneClick({
        ...commonTrackingParams,
        blockedType: EAccessCheckTrackingStatus.Unauthorized,
      });
    }

    const { isConfirmed, trackingStatus } = await checkContactsAccess({
      offerId: offer.id,
      limitType: ESubscriptionLimitType.PhoneNumber,
    });

    if (isConfirmed) {
      trackShowEarlyAccessPhoneClick(commonTrackingParams);
      onShowPhoneClick();

      return;
    }

    if (trackingStatus) {
      trackBlockedEarlyAccessPhoneClick({ ...commonTrackingParams, blockedType: trackingStatus });
    }
  };

  const handleButtonClick: VoidFunction = async () => {
    if (isEarlyAccessContactsFlowEnabled) {
      await handleEarlyAccessFlow();

      return;
    }

    // ДинКТ вторички (SEL-998); деал-рент открывает форму, а не номера
    const clickId = isOfferDynamicCalltrackingEnabled && !isDealRentFormEnabled ? uuid() : undefined;

    trackShowPhone(clickId);

    if (clickId) {
      setDynamicPhoneLoading(true);
      const hasPhoneToShow = await requestPhone({
        offerId: offer.id,
        clickId,
        hasStaticPhones: phones.length > 0,
        onDegradation: () => trackShowStatNumber({ offer, agent, user, clickId }),
      });
      setDynamicPhoneLoading(false);

      if (!hasPhoneToShow) {
        return;
      }
    }

    onShowPhoneClick();
    startChat();
  };

  if (!isPublished) {
    return null;
  }

  if (!phones.length || phones.some(phone => !phone.countryCode || !phone.number)) {
    logger.error(`Offer phones is null`, { cianId: offer.cianId });

    return null;
  }

  return (
    <div data-name="PhonesContainer">
      {(phonesShown || isPrintVersion) && <PhoneContainer placeType="ContactsBottom" />}
      {!phonesShown && !isPrintVersion && (
        <Button
          theme="fill_primary"
          size={size}
          onClick={handleButtonClick}
          fullWidth
          loading={status === EStatus.Loading || isDynamicPhoneLoading}
        >
          {buttonContent}
        </Button>
      )}
      <PopupOwnOfferContainer isOpen={isOwnOfferPopupOpen} onClose={ownOfferPopupClose} />
    </div>
  );
};
